import React, { useState, useEffect } from 'react';
import { Plus, Pencil, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const EMPTY = { name: '', slug: '', published: false, metaTitle: '', metaDescription: '', content: '', faq: [] };

export default function AdminSeoLocal() {
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.getCities(true).then(setCities).catch(() => {});
  }, []);

  const togglePublish = async (c) => {
    try {
      const updated = await api.updateCity(c.id, { published: !c.published });
      setCities((prev) => prev.map((x) => x.id === c.id ? updated : x));
      toast.success(`${c.name} ${updated.published ? 'publicada' : 'despublicada'}`);
    } catch { toast.error('Error al actualizar'); }
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      if (editing.id) {
        const { id, ...data } = editing;
        const updated = await api.updateCity(id, data);
        setCities((prev) => prev.map((c) => c.id === id ? updated : c));
      } else {
        const created = await api.createCity(editing);
        setCities((prev) => [...prev, created]);
      }
      setEditing(null);
      toast.success('Ciudad guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-seo">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-stone-800">SEO Local · Ciudades</h1>
          <p className="text-sm text-stone-500">Cada ciudad publicada genera su página SEO automáticamente (incluida en sitemap.xml).</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-city-button">
          <Plus className="w-4 h-4" /> Nueva ciudad
        </button>
      </div>

      {editing && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-8" data-testid="city-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-stone-800">{editing.id ? `Editar ${editing.name}` : 'Nueva ciudad'}</h2>
            <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Nombre *</label>
              <input value={editing.name} onChange={set('name')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="city-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Slug</label>
              <input value={editing.slug} onChange={set('slug')} placeholder="se genera automáticamente" className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Meta Title</label>
              <input value={editing.metaTitle} onChange={set('metaTitle')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Meta Description</label>
              <input value={editing.metaDescription} onChange={set('metaDescription')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 mb-1">Contenido propio</label>
              <textarea value={editing.content} onChange={set('content')} rows={4} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
            <button onClick={save} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-city-button">Guardar ciudad</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Ciudad</th>
              <th className="px-4 py-3 font-medium">URL</th>
              <th className="px-4 py-3 font-medium">Meta Title</th>
              <th className="px-4 py-3 font-medium w-24">Publicada</th>
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {cities.map((c) => (
              <tr key={c.id} className="border-t border-stone-100" data-testid={`city-row-${c.id}`}>
                <td className="px-4 py-3 font-bold text-stone-800">{c.name}</td>
                <td className="px-4 py-3 text-teal-600 text-xs">/{c.slug}</td>
                <td className="px-4 py-3 text-stone-500 text-xs">{c.metaTitle || '—'}</td>
                <td className="px-4 py-3">
                  <Switch checked={c.published} onCheckedChange={() => togglePublish(c)} data-testid={`toggle-city-${c.id}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditing({ ...c })} className="text-stone-400 hover:text-amber-600 transition-colors" data-testid={`edit-city-${c.id}`}><Pencil className="w-4 h-4" /></button>
                    {c.published && <Link to={`/${c.slug}`} className="text-stone-400 hover:text-teal-600 transition-colors"><ExternalLink className="w-4 h-4" /></Link>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
